// Cricket fielding-position resolver — maps a (sliceIndex, bandIndex) pair
// on the 48 x 6 wagon-wheel grid to one of the 39 cricket-correct fielding
// positions per the operator-supplied spec (2026-05-16, three rings of
// 12 + 12 + 15 positions).
//
// Geometry (matches CricketFieldEngine.jsx + geometry.js):
//   slice 0  =  0 deg (straight toward the bowler — bottom of SVG)
//   slice 12 =  90 deg (off-side square — RH batter, screen-left)
//   slice 24 = 180 deg (directly behind the wicket-keeper — top of SVG)
//   slice 36 = 270 deg (leg-side square — RH batter, screen-right)
//   each slice spans 7.5 deg, angle increases clockwise.
//
// Bands map to the three rings the operator's spec defines:
//   bands 0, 1 → INNER (silly + short close-catchers)
//   band  2   → 30-YARD ring (saving-the-one fielders)
//   bands 3, 4, 5 → BOUNDARY (deep + boundary)
//
// LH batter is handled by mirroring the slice index across the straight line
// (the edge between slices 47 and 0, and between 23 and 24): adjusted =
// 47 - slice. Mirroring angle a gives 360 - a, and slice i spans
// [7.5i, 7.5i + 7.5), so its mirror is slice 47 - i. The old (48 - slice)
// was one slice (7.5 deg) off and mislabelled ~29% of LH lookups at every
// region edge (2026-09-30). The base RH-perspective resolver is then used
// unchanged.

// Three lookup tables, one per ring. Each is a 48-element array indexed by
// sliceIndex. The slice-range layout below mirrors the spec image — Bowler
// and Wicket Keeper occupy the narrow zones straight ahead / directly
// behind the batter and are never offered as "the ball ended up here"
// outputs from the inner / 30-yard rings respectively.

const INNER_BY_SLICE = [
  // Off-side, clockwise from bowler line:
  "Bowler", "Bowler",                                   // 0-1   (straight)
  "Silly Mid Off", "Silly Mid Off", "Silly Mid Off", "Silly Mid Off",  // 2-5
  "Short Extra Cover", "Short Extra Cover", "Short Extra Cover", "Short Extra Cover", // 6-9
  "Short Cover", "Short Cover", "Short Cover", "Short Cover",          // 10-13
  "Silly Point", "Silly Point",                                         // 14-15
  "Gully", "Gully",                                                     // 16-17
  "Slips", "Slips", "Slips", "Slips",                                   // 18-21
  // Behind-the-keeper zone (the Wicket Keeper marker handles this visually):
  "Wicket Keeper", "Wicket Keeper", "Wicket Keeper", "Wicket Keeper",   // 22-25
  // Leg-side, clockwise back to bowler:
  "Leg Slip", "Leg Slip", "Leg Slip", "Leg Slip",                       // 26-29
  "Leg Gully", "Leg Gully",                                             // 30-31
  "Short Backward Square Leg", "Short Backward Square Leg",             // 32-33
  "Short Leg", "Short Leg", "Short Leg", "Short Leg",                   // 34-37
  "Short Mid Wicket", "Short Mid Wicket", "Short Mid Wicket", "Short Mid Wicket", // 38-41
  "Silly Mid On", "Silly Mid On", "Silly Mid On", "Silly Mid On",       // 42-45
  "Bowler", "Bowler",                                                   // 46-47 (straight)
];

const RING_BY_SLICE = [
  // 30-yard ring (band 2). Symmetric across the bowler-keeper axis so
  // slice 36 (the perfectly-square leg-side angle, 270°) lands on
  // Square Leg, not Mid Wicket.
  "Mid Off", "Mid Off", "Mid Off", "Mid Off",                           // 0-3
  "Extra Cover", "Extra Cover", "Extra Cover", "Extra Cover",           // 4-7
  "Cover", "Cover", "Cover",                                            // 8-10
  "Cover Point", "Cover Point", "Cover Point",                          // 11-13
  "Point", "Point",                                                     // 14-15
  "Backward Point", "Backward Point",                                   // 16-17
  "Short Third", "Short Third", "Short Third", "Short Third",           // 18-21
  // Keeper zone — no 30-yard fielding position here; the WK marker
  // sits on top and clicks fall through to the surrounding sectors.
  "Wicket Keeper", "Wicket Keeper", "Wicket Keeper", "Wicket Keeper",   // 22-25
  "Short Fine Leg", "Short Fine Leg", "Short Fine Leg", "Short Fine Leg", // 26-29
  "Backward Square Leg", "Backward Square Leg",                          // 30-31
  "Square Leg", "Square Leg", "Square Leg", "Square Leg",                // 32-35  (≈ 240°-270°)
  "Mid Wicket", "Mid Wicket", "Mid Wicket", "Mid Wicket",                // 36-39  (≈ 270°-300°)
  "Mid On", "Mid On", "Mid On", "Mid On", "Mid On", "Mid On", "Mid On", "Mid On", // 40-47 (≈ 300°-360°)
];

const BOUNDARY_BY_SLICE = [
  // Boundary ring (bands 3-5). "Straight" is the 2-slice wedge centred
  // on the bowler line (slices 47 + 0), so the lookup wraps.
  "Straight",                                                            // 0
  "Long Off", "Long Off", "Long Off",                                    // 1-3
  "Deep Extra Cover", "Deep Extra Cover", "Deep Extra Cover", "Deep Extra Cover", // 4-7
  "Deep Cover", "Deep Cover", "Deep Cover", "Deep Cover",                // 8-11
  "Deep Point", "Deep Point", "Deep Point",                              // 12-14
  "Deep Backward Point", "Deep Backward Point", "Deep Backward Point",   // 15-17
  "Third Man", "Third Man", "Third Man", "Third Man", "Third Man",       // 18-22
  // Long Stop is the narrow position directly behind the keeper.
  "Long Stop", "Long Stop", "Long Stop",                                 // 23-25
  "Deep Fine Leg", "Deep Fine Leg", "Deep Fine Leg",                     // 26-28
  "Long Leg", "Long Leg", "Long Leg",                                    // 29-31
  "Deep Backward Square Leg", "Deep Backward Square Leg", "Deep Backward Square Leg", // 32-34
  "Deep Square Leg", "Deep Square Leg", "Deep Square Leg",               // 35-37
  "Deep Mid Wicket", "Deep Mid Wicket", "Deep Mid Wicket",               // 38-40
  "Cow Corner", "Cow Corner", "Cow Corner",                              // 41-43
  "Long On", "Long On", "Long On",                                       // 44-46
  "Straight",                                                            // 47
];

function ringForBand(band) {
  if (band <= 1) return INNER_BY_SLICE;
  if (band === 2) return RING_BY_SLICE;
  return BOUNDARY_BY_SLICE;            // bands 3, 4, 5
}

function resolveRH(slice, band) {
  const sl = ((slice % 48) + 48) % 48;
  const b = Math.max(0, Math.min(5, band));
  const table = ringForBand(b);
  return table[sl];
}

export function resolvePosition(sliceIndex, bandIndex, batterHand = "RIGHT") {
  if (sliceIndex == null || bandIndex == null) return "Unknown";
  const adjusted = batterHand === "LEFT" ? 47 - (((sliceIndex % 48) + 48) % 48) : sliceIndex;
  return resolveRH(adjusted, bandIndex);
}

// Exported for tooling / tests — the full 39-position spec the wagon
// wheel can return (plus Bowler + Wicket Keeper as central markers).
export const ALL_POSITIONS = [
  // 12 inner
  "Slips", "Gully", "Leg Gully", "Leg Slip", "Short Backward Square Leg",
  "Short Cover", "Short Extra Cover", "Short Leg", "Short Mid Wicket",
  "Silly Mid Off", "Silly Mid On", "Silly Point",
  // 12 thirty-yard ring
  "Backward Point", "Backward Square Leg", "Cover", "Cover Point",
  "Extra Cover", "Mid Off", "Mid On", "Mid Wicket", "Point",
  "Short Fine Leg", "Short Third", "Square Leg",
  // 15 boundary
  "Cow Corner", "Deep Backward Point", "Deep Backward Square Leg",
  "Deep Cover", "Deep Extra Cover", "Deep Fine Leg", "Deep Mid Wicket",
  "Deep Point", "Deep Square Leg", "Long Leg", "Long Off", "Long On",
  "Long Stop", "Straight", "Third Man",
];
